import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Trophy, Target, Zap, ChevronRight, BookOpen, Shield, 
  Loader2, AlertTriangle, CheckCircle2, XCircle, Clock,
  TrendingUp, Send, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import PowerScoreShareCard from '../components/PowerScoreShareCard';

// Coach's Notes based on score thresholds
const getCoachNote = (pillar, score) => {
  const notes = {
    academic: {
      low: "Eligibility Risk Detected. Immediate Pivot required.",
      high: "Foundation Strong. Cleared for recruitment."
    },
    tactical: {
      low: "Processing speed below elite standard.",
      high: "IQ matches the physical stats."
    },
    brand: {
      low: "Brand Liability. Public profile needs governance.",
      high: "Media Ready. Legacy built."
    }
  };
  
  return score >= 80 ? notes[pillar].high : notes[pillar].low;
};

// Get overall clearance status
const getClearanceStatus = (academic, tactical, brand) => {
  const avgScore = (academic + tactical + brand) / 3;
  const allAbove80 = academic >= 80 && tactical >= 80 && brand >= 80;
  const allAbove60 = academic >= 60 && tactical >= 60 && brand >= 60;
  
  if (allAbove80) {
    return {
      status: 'OFFICIALLY CLEARED',
      color: 'green',
      icon: CheckCircle2,
      description: 'You meet all requirements for the Winter \'26 Cohort.'
    };
  } else if (allAbove60) {
    return {
      status: 'PROVISIONAL',
      color: 'yellow',
      icon: Clock,
      description: 'You\'re close but need improvement in key areas before clearance.'
    };
  } else {
    return {
      status: 'NOT CLEARED',
      color: 'red',
      icon: XCircle,
      description: 'Critical gaps detected. 30-Day Sprint required for clearance.'
    };
  }
};

// Get weakest pillar
const getWeakestPillar = (academic, tactical, brand) => {
  const pillars = [
    { name: 'Academic Foundations', score: academic, id: 'academic' },
    { name: 'Tactical Logic', score: tactical, id: 'tactical' },
    { name: 'Legacy Branding', score: brand, id: 'brand' }
  ];
  return pillars.reduce((min, p) => p.score < min.score ? p : min, pillars[0]);
};

// Get recommended pivot path
const getRecommendedPath = (academic, brand) => {
  if (academic < 80 && academic <= brand) {
    return {
      path: 'Academic Power Sprint',
      badge: 'Recommended for Eligibility',
      id: 'academic'
    };
  } else if (brand < 80) {
    return {
      path: 'Legacy Branding Pivot',
      badge: 'Recommended for NIL Readiness',
      id: 'brand'
    };
  }
  return null;
};

const PerformersAudit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [improvement, setImprovement] = useState(null);
  const [parentNotified, setParentNotified] = useState(false);

  const results = location.state;

  useEffect(() => {
    if (results && !scoreSaved) {
      savePowerScore();
    }
  }, [results]);

  const savePowerScore = async () => {
    if (!results) return;
    
    try {
      const response = await api.post('/academy/power-score/save', {
        email: results.userInfo.email,
        name: results.userInfo.name,
        academic_score: results.categoryScores.academic,
        tactical_score: results.categoryScores.sports,
        brand_score: results.categoryScores.brand,
        total_score: results.score,
        major: results.major?.name || 'Scholar Athlete',
        cohort: "Winter '26"
      });
      
      setScoreSaved(true);
      
      if (response.data.is_retake) {
        setImprovement({
          points: response.data.improvement,
          percent: response.data.improvement_pct,
          previous: response.data.previous_score
        });
        toast.success(`Score improved by ${response.data.improvement} points!`);
      }

      // Send parent notification
      if (results.userInfo.parentEmail) {
        notifyParent();
      }
    } catch (error) {
      console.error('Failed to save power score:', error);
    }
  };

  const notifyParent = async () => {
    try {
      await api.post('/academy/notify-parent', {
        student_name: results.userInfo.name,
        student_email: results.userInfo.email,
        parent_email: results.userInfo.parentEmail,
        academic_score: results.categoryScores.academic,
        tactical_score: results.categoryScores.sports,
        brand_score: results.categoryScores.brand,
        total_score: results.score,
        clearance_status: getClearanceStatus(
          results.categoryScores.academic,
          results.categoryScores.sports,
          results.categoryScores.brand
        ).status
      });
      setParentNotified(true);
      toast.success('Parent has been notified of your results');
    } catch (error) {
      console.error('Failed to notify parent:', error);
    }
  };

  useEffect(() => {
    if (!results) {
      navigate('/academy/diagnostic');
    }
  }, [results, navigate]);

  if (!results) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const { score, categoryScores, userInfo, major } = results;
  const academic = categoryScores.academic;
  const tactical = categoryScores.sports;
  const brand = categoryScores.brand;
  
  const clearance = getClearanceStatus(academic, tactical, brand);
  const weakestPillar = getWeakestPillar(academic, tactical, brand);
  const recommendedPath = getRecommendedPath(academic, brand);
  const ClearanceIcon = clearance.icon;

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500/10 border-green-500/30';
    if (score >= 60) return 'bg-yellow-500/10 border-yellow-500/30';
    return 'bg-red-500/10 border-red-500/30';
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20 pb-12">
      {/* Improvement Banner */}
      {improvement && (
        <div className="bg-gradient-to-r from-green-600/20 via-green-500/20 to-green-600/20 border-b border-green-500/30 py-4 px-4">
          <div className="max-w-4xl mx-auto text-center flex items-center justify-center gap-3">
            <TrendingUp className="w-6 h-6 text-green-500" />
            <p className="text-green-400 font-bold">
              Score improved by {improvement.points} points from your previous {improvement.previous}!
            </p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-gray-900 via-gray-900/50 to-black py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Scholar Athlete Logo */}
          <img 
            src="/images/scholar-athlete-logo-new.png" 
            alt="Scholar Athlete Academy" 
            className="h-24 md:h-32 mx-auto mb-6"
          />
          
          <h1 className="text-4xl md:text-5xl font-black mb-2">
            POWER SCORE <span className="text-orange-500">LOCKED.</span>
          </h1>
          <p className="text-xl text-gray-400 mb-8">
            Your Diagnostic results are in. Are you Officially Cleared?
          </p>

          {/* Score Display */}
          <div className="relative inline-block mb-8">
            <div className="w-40 h-40 relative">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="80" cy="80" r="72" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-800" />
                <circle cx="80" cy="80" r="72" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray={`${(score / 100) * 452} 452`} className="text-orange-500 transition-all duration-1000" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-4xl font-black ${getScoreColor(score)}`}>{score}</span>
                <span className="text-gray-500 text-xs font-bold tracking-wider">POWER SCORE</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">
        {/* The Breakdown */}
        <h2 className="text-2xl font-black text-white mb-6 text-center">THE BREAKDOWN</h2>
        
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {/* Academic Foundations */}
          <Card className={`${getScoreBg(academic)} border-2`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Academic Foundations</p>
                  <p className={`text-3xl font-black ${getScoreColor(academic)}`}>{academic}%</p>
                </div>
              </div>
              <div className="border-t border-gray-700 pt-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Coach's Note:</p>
                <p className={`text-sm font-medium ${academic >= 80 ? 'text-green-400' : 'text-red-400'}`}>
                  "{getCoachNote('academic', academic)}"
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Tactical Logic */}
          <Card className={`${getScoreBg(tactical)} border-2`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Tactical Logic</p>
                  <p className={`text-3xl font-black ${getScoreColor(tactical)}`}>{tactical}%</p>
                </div>
              </div>
              <div className="border-t border-gray-700 pt-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Coach's Note:</p>
                <p className={`text-sm font-medium ${tactical >= 80 ? 'text-green-400' : 'text-red-400'}`}>
                  "{getCoachNote('tactical', tactical)}"
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Legacy Branding */}
          <Card className={`${getScoreBg(brand)} border-2`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Legacy Branding</p>
                  <p className={`text-3xl font-black ${getScoreColor(brand)}`}>{brand}%</p>
                </div>
              </div>
              <div className="border-t border-gray-700 pt-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Coach's Note:</p>
                <p className={`text-sm font-medium ${brand >= 80 ? 'text-green-400' : 'text-red-400'}`}>
                  "{getCoachNote('brand', brand)}"
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Overall Status */}
        <Card className={`border-2 mb-8 ${
          clearance.color === 'green' ? 'bg-green-500/10 border-green-500/50' :
          clearance.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/50' :
          'bg-red-500/10 border-red-500/50'
        }`}>
          <CardContent className="p-8 text-center">
            <ClearanceIcon className={`w-16 h-16 mx-auto mb-4 ${
              clearance.color === 'green' ? 'text-green-500' :
              clearance.color === 'yellow' ? 'text-yellow-500' :
              'text-red-500'
            }`} />
            <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-2">Overall Status</h3>
            <p className={`text-4xl font-black mb-2 ${
              clearance.color === 'green' ? 'text-green-500' :
              clearance.color === 'yellow' ? 'text-yellow-500' :
              'text-red-500'
            }`}>
              {clearance.status}
            </p>
            <p className="text-gray-400">{clearance.description}</p>
          </CardContent>
        </Card>

        {/* The Next Move - Only show if not fully cleared */}
        {clearance.status !== 'OFFICIALLY CLEARED' && (
          <Card className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-2 border-red-500/30 mb-8">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap className="w-7 h-7 text-red-500" />
                </div>
                <div className="flex-1">
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mb-3">
                    SPRINT REQUIRED
                  </Badge>
                  <h3 className="text-2xl font-black text-white mb-2">
                    Your {weakestPillar.name} is at <span className="text-red-500">{weakestPillar.score}%</span>
                  </h3>
                  <p className="text-gray-300 mb-4 text-lg">
                    To be eligible for the Winter Circuit, you are <span className="text-white font-bold">required</span> to 
                    enter the <span className="text-orange-500 font-bold">{recommendedPath?.path || '30-Day Sprint'}</span>.
                  </p>
                  
                  {recommendedPath && (
                    <div className="mb-6 p-4 bg-black/40 rounded-lg border border-orange-500/30">
                      <Badge className="bg-orange-500 text-black font-bold mb-2">
                        {recommendedPath.badge}
                      </Badge>
                      <p className="text-white font-bold text-lg">{recommendedPath.path}</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {recommendedPath.id === 'academic' 
                          ? 'Heavy integration with the IXL Engine to master Math/ELA modules for your grade level.'
                          : 'Developing your "Standard-Bearer" brand through media training and social media governance.'}
                      </p>
                    </div>
                  )}

                  {/* What is the Sprint? */}
                  <div className="mb-6 p-4 bg-orange-500/5 rounded-lg border border-orange-500/20">
                    <p className="text-gray-400 text-sm leading-relaxed">
                      <span className="text-orange-500 font-bold">"</span>
                      The Sprint isn't just a course; it's a commitment. For 30 days, we treat your academics and your brand 
                      with the same intensity you bring to the court. At the end of 30 days, you have a Power Score that 
                      proves to scouts and recruiters that you are a professional.
                      <span className="text-orange-500 font-bold">"</span>
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => navigate('/academy/grandmaster', { state: { recommended: recommendedPath?.id } })}
                    className="bg-orange-500 hover:bg-orange-600 text-black font-black text-lg px-10 py-6 uppercase tracking-wider"
                    data-testid="enroll-sprint-btn"
                  >
                    Enroll in the Sprint
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cleared - Celebration */}
        {clearance.status === 'OFFICIALLY CLEARED' && (
          <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/5 border-2 border-green-500/30 mb-8">
            <CardContent className="p-8 text-center">
              <Trophy className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-black text-green-500 mb-2">CONGRATULATIONS!</h3>
              <p className="text-gray-300 mb-6">
                You've demonstrated elite-level readiness across all three pillars. 
                You're officially cleared for the Winter '26 Cohort.
              </p>
              <Button
                onClick={() => navigate('/academy/enroll')}
                className="bg-green-500 hover:bg-green-600 text-black font-black text-lg px-8 py-6 uppercase tracking-wider"
              >
                Secure Your Roster Spot
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Share Your Score */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-white mb-4 text-center">Share Your Results</h3>
          <PowerScoreShareCard
            userName={userInfo.name}
            totalScore={score}
            academicScore={academic}
            tacticalScore={tactical}
            brandScore={brand}
            major={major?.name || 'Scholar Athlete'}
            cohort="Winter '26"
            isParent={false}
          />
        </div>

        {/* Parent Notification Status */}
        {parentNotified && (
          <div className="text-center mb-8 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <div className="flex items-center justify-center gap-2 text-purple-400">
              <Send className="w-5 h-5" />
              <p className="font-medium">Your parent has been notified of your results via email.</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/academy')}
            variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            Return to Academy
          </Button>
          <Button
            onClick={() => navigate('/academy/diagnostic')}
            variant="outline"
            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
          >
            Retake Diagnostic
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PerformersAudit;
